// ─── Preset tables ────────────────────────────────────────────────────────────
/**
 * Standard architectural scales.
 * Value = paper inches per real foot (e.g. 1/4" = 1' → 0.25 in/ft).
 */
export const ARCH_SCALES = {
    '1/16" = 1\'-0"': 1 / 16,
    '3/32" = 1\'-0"': 3 / 32,
    '1/8" = 1\'-0"': 1 / 8,
    '3/16" = 1\'-0"': 3 / 16,
    '1/4" = 1\'-0"': 1 / 4,
    '3/8" = 1\'-0"': 3 / 8,
    '1/2" = 1\'-0"': 1 / 2,
    '3/4" = 1\'-0"': 3 / 4,
    '1" = 1\'-0"': 1,
    '1-1/2" = 1\'-0"': 1.5,
    '3" = 1\'-0"': 3,
};
/**
 * Standard engineering scales (civil/site plans).
 * Value = paper inches per real foot.
 */
export const ENGR_SCALES = {
    '1" = 10\'': 1 / 10,
    '1" = 20\'': 1 / 20,
    '1" = 30\'': 1 / 30,
    '1" = 40\'': 1 / 40,
    '1" = 50\'': 1 / 50,
    '1" = 60\'': 1 / 60,
    '1" = 100\'': 1 / 100,
    '1" = 200\'': 1 / 200,
};
// ─── Core formula ─────────────────────────────────────────────────────────────
/**
 * pixelsPerFoot = dpi × paperInchesPerRealFoot
 *
 * Derivation:
 *   dpi pixels = 1 inch on paper
 *   1 inch on paper = (1 / paperInchesPerRealFoot) real feet
 *   → dpi pixels = (1 / paperInchesPerRealFoot) real feet
 *   → pixelsPerFoot = dpi × paperInchesPerRealFoot
 */
export function pixelsPerFoot(dpi, paperInchesPerRealFoot) {
    if (dpi <= 0)
        throw new Error('dpi must be > 0');
    if (paperInchesPerRealFoot <= 0)
        throw new Error('paperInchesPerRealFoot must be > 0');
    return dpi * paperInchesPerRealFoot;
}
// ─── Scale string parsing ─────────────────────────────────────────────────────
/**
 * Parse a fraction string like '1/4', '3/16', or '1-1/2' to a decimal.
 * Returns null if the string is not a recognisable fraction/integer.
 */
export function parseFraction(s) {
    s = s.trim();
    // Mixed number: '1-1/2'
    const mixed = s.match(/^(\d+)-(\d+)\/(\d+)$/);
    if (mixed) {
        const whole = parseInt(mixed[1], 10);
        const num = parseInt(mixed[2], 10);
        const den = parseInt(mixed[3], 10);
        if (den === 0)
            return null;
        return whole + num / den;
    }
    // Fraction: '3/16'
    const frac = s.match(/^(\d+)\/(\d+)$/);
    if (frac) {
        const num = parseInt(frac[1], 10);
        const den = parseInt(frac[2], 10);
        if (den === 0)
            return null;
        return num / den;
    }
    // Whole number or decimal: '1', '0.25'
    const num = parseFloat(s);
    return isNaN(num) ? null : num;
}
/**
 * Parse a scale notation string extracted from a title block into a ParsedScale.
 *
 * Handles formats such as:
 *   '1/4" = 1\'-0"'    (architectural, feet-inches RHS)
 *   '1/4"=1\''          (architectural, feet-only RHS)
 *   '1" = 20\''         (engineering)
 *   'SCALE: 1/4"=1\''   (with prefix)
 *   '1:48'              (ratio form)
 *
 * Returns null if the string cannot be recognised.
 */
export function parseScaleString(raw) {
    const s = raw.trim().replace(/^scale[:\s]*/i, '');
    // ── Ratio form  1:48 ──────────────────────────────────────────────────────
    const ratioMatch = s.match(/^1\s*:\s*(\d+(?:\.\d+)?)$/);
    if (ratioMatch) {
        const denominator = parseFloat(ratioMatch[1]);
        // 1:48 means 1 inch paper = 48 inches real = 4 feet → 1/48 in/in → 1/4 in/ft
        const paperInchesPerRealFoot = 12 / denominator;
        return { paperInchesPerRealFoot, scaleString: s };
    }
    // ── General  <paper>" = <real>'[-0"] ──────────────────────────────────────
    // Capture: paper side (inches fraction), real side (feet, optional -0" suffix)
    const archMatch = s.match(/^([0-9][0-9\-/]*)\s*"\s*=\s*([0-9][0-9/]*)\s*'(?:-\s*0\s*")?$/);
    if (archMatch) {
        const paper = parseFraction(archMatch[1]);
        const real = parseFraction(archMatch[2]);
        if (paper !== null && real !== null && paper > 0 && real > 0) {
            return { paperInchesPerRealFoot: paper / real, scaleString: s };
        }
    }
    // ── Lookup in preset tables ───────────────────────────────────────────────
    for (const [key, val] of Object.entries({ ...ARCH_SCALES, ...ENGR_SCALES })) {
        if (key.replace(/\s/g, '') === s.replace(/\s/g, '')) {
            return { paperInchesPerRealFoot: val, scaleString: key };
        }
    }
    return null;
}
/**
 * Attempt to find a scale notation anywhere in a block of text (e.g. OCR output
 * from a title block).  Returns the first match found, or null.
 */
export function extractScaleFromText(text) {
    // Patterns to try, in priority order
    const patterns = [
        // Ratio:  1:48,  1 : 100
        /\b1\s*:\s*\d+(?:\.\d+)?\b/g,
        // Arch:   1/4" = 1'-0",  3/4"=1',  1"=1'-0"
        /[0-9][0-9\-/]*\s*"\s*=\s*[0-9][0-9/]*\s*'(?:\s*-\s*0\s*")?/g,
    ];
    for (const re of patterns) {
        let m;
        while ((m = re.exec(text)) !== null) {
            const parsed = parseScaleString(m[0]);
            if (parsed !== null)
                return parsed;
        }
    }
    return null;
}
// ─── SheetScale constructors ──────────────────────────────────────────────────
/** Build a SheetScale from a preset scale key and the image DPI. */
export function sheetScaleFromPreset(key, dpi) {
    const all = { ...ARCH_SCALES, ...ENGR_SCALES };
    const paperInchesPerRealFoot = all[key];
    if (paperInchesPerRealFoot === undefined) {
        throw new Error(`Unknown preset scale: "${key}"`);
    }
    return {
        pixelsPerFoot: pixelsPerFoot(dpi, paperInchesPerRealFoot),
        dpi,
        source: 'preset',
        scaleString: key,
    };
}
/** Build a SheetScale from a parsed scale and DPI. */
export function sheetScaleFromParsed(parsed, dpi, source = 'extracted') {
    return {
        pixelsPerFoot: pixelsPerFoot(dpi, parsed.paperInchesPerRealFoot),
        dpi,
        source,
        scaleString: parsed.scaleString,
    };
}
/**
 * Build a SheetScale by click-calibration: two pixel points on the image
 * and the known real distance between them in feet.
 */
export function sheetScaleFromCalibration(p1, p2, knownFeet) {
    if (knownFeet <= 0)
        throw new Error('knownFeet must be > 0');
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const pixelDist = Math.sqrt(dx * dx + dy * dy);
    if (pixelDist === 0)
        throw new Error('calibration points must be distinct');
    return {
        pixelsPerFoot: pixelDist / knownFeet,
        // DPI is not known from calibration alone; leave as 0 sentinel
        dpi: 0,
        source: 'calibrated',
    };
}
